import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CreateReportComponent } from '../../components/chart-data/chart-data';
import { ReportService } from '../../services/report.service';
import { ReportStatus } from '../../models/report';
import { Router } from '@angular/router';
import { Loader } from '../../loader/loader';
import { SnackbarService } from '../../services/snackbar.service';
import { DialogueBox } from '../../dialogue-box/dialogue-box';
import { AuthService } from '../../auth/auth.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  CellClickedEvent,
  GridOptions,
} from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { DialogueTexts } from '../../constants/dialog-texts.constant';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { ApiResponseMessages } from '../../constants/api-response-messages.constants';

@Component({
  selector: 'app-report-list',
  standalone: true,
  imports: [
    CommonModule,
    CreateReportComponent,
    Loader,
    DialogueBox,
    FormsModule,
    ReactiveFormsModule,
    AgGridAngular,
  ],
  templateUrl: './report-list.html',
  styleUrl: './report-list.scss',
})
export class ReportList {
  private authService = inject(AuthService);
  private reportService = inject(ReportService);
  private router = inject(Router);
  private snackbar = inject(SnackbarService);
  isLoading = false;
  reports = this.reportService.getRecentReports();
  currentUserName!: string | null;
  selectedReportId: number | 0 = 0;
  isClosing = false;
  projectId: number | null = null;
  reportTypeId: number | null = null;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  isDialogOpen = false;
  reportTypes = this.reportService.getReportTypes();
  isFilterEnabled = false;
  name: string = '';
  dialogTexts = DialogueTexts;
  validationMessages = ValidationMessagesTexts;
  apiResponseMessages = ApiResponseMessages;

  ngOnInit(): void {
    this.authService.getCurrentAccount().then(currentAccount => {
      if (currentAccount) {
        this.name = currentAccount.name || ''
        const parts = this.name.split(/\s+/).filter(Boolean).slice(0, 2);
        this.name = parts.reverse().join(' ').replace(/,$/, '').trim();
      }
    })
    this.reportService.loadReports();
  }

  

  columnDefs: ColDef[] = [
    { headerName: 'Report Name', field: 'reportName'},
    { headerName: 'Project ID', field: 'projectId' },
    { headerName: 'Client Name', field: 'clientName' },
    { headerName: 'Site Name', field: 'siteName' },

    {
      headerName: 'Date Created',
      field: 'createdDate',
      valueFormatter: (p: ValueFormatterParams) =>
        new Date(p.value as string).toLocaleDateString('en-US'),
    },

    { headerName: 'Owner', field: 'owner' },

    {
      headerName: 'Status',
      cellRenderer: (params: ICellRendererParams) => {
        const statusValue = params.data.status;
        const status = params.context.componentParent.getStatusDisplay(params.data.status);
        const icons = params.context.componentParent
          .getStatusDotsIcons(statusValue)
          .map((icon: string) => `<img src="/assets/icons/${icon}" width="3" height="3" />`)
          .join('');

        const statusClass = statusValue === 6 ? 'status-red' : '';
        return `
          <div class="status">
            <span class="status-text ${statusClass}">${status}</span>
            <div class="dots">${icons}</div>
          </div>
        `;
      }, filter: false
    }
    ,
    {
      headerName: 'Actions',
      cellRenderer: (params: ICellRendererParams) => {
        const status = params.context.componentParent.getStatusDisplay(params.data.status);
        const pdf = params.data.pdfFilePath;

        return `
          <div class="actions">
    
            <!-- Edit Button -->
            <button class="edit"
              data-action="edit"
              ${status === 'Approved' ? 'disabled' : ''}>
              <img src="/assets/icons/edit_32.svg" />
            </button>
    
            <!-- Delete Button -->
            ${status !== 'Approved' && status !== 'Published'
            ? `<button data-action="delete">
                  <img src="/assets/icons/delete_32.svg" />
               </button>`
            : ''}
    
            <!-- DOC + PDF Buttons (Approved only) -->
            ${status === 'Approved'
            ? `
                  <button data-action="doc"
                    ${!pdf ? 'disabled title="Report Generation In Progress"' : ''}>
                    <img src="/assets/icons/word.svg" />
                  </button>
    
                  <button data-action="pdf"
                    ${!pdf ? 'disabled title="Report Generation In Progress"' : ''}>
                    <img src="/assets/icons/pdf.svg" />
                  </button>
                `
            : ''
          }
    
          </div>
        `;
      }

      ,
      filter: false,

      onCellClicked: (params: CellClickedEvent) => {
        const event = params.event as Event | null;
        if (!event) return; 

        const target = event.target as HTMLElement | null;
        if (!target) return;

        const btn = target.closest('button');
        if (!btn) return;

        const action = btn.getAttribute('data-action');
        if (!action) return;

        switch (action) {
          case 'edit':
            params.context.componentParent.editReport(params.data.reportId);
            break;

          case 'delete':
            params.context.componentParent.openDialog(params.data.reportId);
            break;
          case 'doc':
            params.context.componentParent.previewDoc(
              params.data.pdfFilePath,    // preview PDF
              params.data.reportName,
              params.data.docFilePath     // download Word file
            );
            break;

          case 'pdf':
            params.context.componentParent.previewDoc(
              params.data.pdfFilePath,    // preview PDF
              params.data.reportName,
              ''     // download PDF file
            );
            break;
        }
      },
    },
  ];



  gridOptions: GridOptions = {
    rowHeight: 55,
    rowStyle: {
      'border-bottom': '10px solid #ffffff',
    },
    domLayout: "normal"
  };

  getStatusDotsIcons(status: number): string[] {
    const dots: string[] = [];

    for (let i = 1; i <= 4; i++) {
      if (status === 4) {
        dots.push('statusicon_green.svg');
      } else if (status === 3) {
        dots.push(i <= 3 ? 'statusicon_yellow.svg' : 'statusicon_white.svg');
      } else if (status === 2) {
        dots.push(i <= 2 ? 'statusicon_orange.svg' : 'statusicon_white.svg');
      } else if (status === 1) {
        dots.push(i <= 1 ? 'statusicon_black.svg' : 'statusicon_white.svg');
      }
      else {
        dots.push('statusicon_white.svg');
      }
    }

    return dots;
  }

  async downloadFile(filePath: string, fileName: string) {
    const response = await fetch(filePath, {
      method: 'GET'
    });

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.docx`;
    link.click();

    window.URL.revokeObjectURL(url);
  }



  loadReports() {
    this.reportService.loadReports();
  }

  onDateRangeChange(event: { fromDate: Date | null; toDate: Date | null }) {
    this.fromDate = event.fromDate;
    this.toDate = event.toDate;
  }

  formatDate(date: Date | null): string | null {
    if (!date) return null;
    return date.toISOString();
  }

  enableGridFilters() {
    this.isFilterEnabled = !this.isFilterEnabled;
  }

  openDialog(reportId: number) {
    this.isDialogOpen = true;
    this.selectedReportId = reportId;
  }

  closeDialog() {
    this.isDialogOpen = false;
  }

  getStatusDisplay(status: ReportStatus): string {
    return this.reportService.getStatusDisplay(status);
  }
  editReport(reportId: number) {
    this.router.navigate(['/report-sections'], {
      queryParams: { reportId: reportId},
    });
  }

  deleteReport(reportId: number) {
    this.isDialogOpen = false;
    this.isLoading = true;
    this.reportService.deleteReport(reportId).subscribe({
      next: (response) => {
        this.loadReports();
        this.isLoading = false;
        this.snackbar.showCustom({
          type: 'success',
          title: this.validationMessages.TOASTER.SUCCESS.TITLE,
          message: response.message,
        });
      },
      error: (err) => {
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.apiResponseMessages.FAILED_TO_DELETE_REPORT,
        });
        this.isLoading = false;
      },
    });
  }

 
  previewDoc(documentUrl: string, reportname: string, wordDocumentPath?: string) {
    this.router.navigate(['/preview-report'], {
      queryParams: {
        documentUrl: encodeURIComponent(documentUrl),   
        reportname: reportname,
        downloadOverrideUrl: wordDocumentPath    
          ? encodeURIComponent(wordDocumentPath)
          : undefined
      },
      state: { fromPdfViewer: true, returnUrl: this.router.url },
    });
  }
  

}
